import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/** @type {typeof import('swiper').default | null} */
let cachedSwiper = null;

/**
 * Dynamically imports Swiper and caches the constructor.
 * @returns {Promise<typeof import('swiper').default>}
 */
const loadSwiper = async () => {
  if (!cachedSwiper) {
    const { default: Swiper } = await import('swiper');
    cachedSwiper = Swiper;
  }
  return cachedSwiper;
};

/**
 * Slideshow custom element powered by Swiper.js.
 *
 * Maintains the same public API as the original Horizon slideshow
 * so that theme-editor.js, product-card.js, media-gallery.js, and
 * collection-links.js continue to work without changes.
 *
 * Configuration is automatic based on context:
 * - Multi-slide carousels: cssMode + slidesPerView auto
 * - Single-slide slideshows (hero, announcements): transform-based with optional loop
 *
 * NOTE: The cards section uses its own custom element <card-carousel> in
 * card-carousel.js — it does NOT use this slideshow-component. See that
 * file for transform-based loop + per-slide video orchestration.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        if (!this.isConnected || !this.#swiper || !this.refs.slides) return;

        const index = parseInt(newValue, 10) || 0;
        const slideId = this.refs.slides[index]?.getAttribute('slide-id');

        if (slideId) {
          this.select({ id: slideId }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  /** @type {InstanceType<typeof import('swiper').default> | null} */
  #swiper = null;

  /** @type {number} */
  #current = 0;

  /** @type {number | undefined} */
  #interval;

  /** @type {boolean} */
  #isMultiSlide = false;


  /** @type {Object | null} Stored Swiper config for lazy init from dormant state */
  #pendingConfig = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resizeTimeout;

  connectedCallback() {
    super.connectedCallback();

    if (this.disabled) return;

    const slideCount = this.slides?.length || 0;

    if (slideCount <= 1) {
      this.#setupSlideshowWithoutControls();
      return;
    }

    this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#swiper) {
      // cleanStyles=true so inline styles set by Swiper (margin-right for spaceBetween,
      // wrapper transform, etc.) are removed — otherwise reinit can stack styles and
      // produce double-gaps or stale layout.
      this.#swiper.destroy(true, true);
      this.#swiper = null;
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    clearTimeout(this.#resizeTimeout);

    this.suspend();
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  // ===========================================================================
  // Swiper initialization
  // ===========================================================================

  #setupSlideshowWithoutControls() {
    this.current = 0;

    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }

      const arrows = this.querySelector('slideshow-arrows');
      if (arrows) arrows.hidden = true;
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  async #setupSlideshow() {
    const { scroller } = this.refs;
    if (!scroller) return;

    const isMultiSlide = this.hasAttribute('multi-slide');
    this.#isMultiSlide = isMultiSlide;
    const wantsLoop = this.loop;
    const wantsRewind = this.infinite && !this.loop;

    // Duplicate slides before init if loop needs more to fill the viewport
    if (wantsLoop) {
      this.#ensureLoopSlides(scroller);
    }

    const config = {
      wrapperClass: 'swiper-wrapper',
      slideClass: 'swiper-slide',
      initialSlide: this.initialSlideIndex,
      speed: 500,
      grabCursor: true,
      simulateTouch: true,
      watchSlidesProgress: true,
      watchOverflow: false,
      shortSwipes: true,
      threshold: 15,
      touchAngle: 35,
      touchStartPreventDefault: false,
      preventInteractionOnTransition: false,
      touchReleaseOnEdges: true,
      mousewheel: {
        forceToAxis: true,
      },
      on: {
        slideChange: () => this.#onSlideChange(),
        slideChangeTransitionEnd: () => this.#onTransitionEnd(),
        transitionStart: () => this.setAttribute('transitioning', ''),
        resize: () => {
          this.#updateSlidesPerGroup();
          this.#updateSlidesFit();
        },
      },
    };

    if (isMultiSlide) {
      // Multi-slide carousels (resource lists, carousel-content, product cards)
      const desktopGap = parseInt(this.getAttribute('data-slide-gap'), 10) || 0;
      const mobileGap = Math.min(desktopGap, 12);
      const centerMobile = this.hasAttribute('data-center-mobile');
      const centered = this.hasAttribute('data-centered');

      const offsetAfter = parseInt(this.getAttribute('data-slides-offset-after'), 10) || 0;

      Object.assign(config, {
        slidesPerView: 'auto',
        spaceBetween: mobileGap,
        ...(offsetAfter && { slidesOffsetAfter: offsetAfter }),
        ...((centered || centerMobile) && { centeredSlides: true, centeredSlidesBounds: true }),
        breakpoints: {
          750: {
            spaceBetween: desktopGap,
            centeredSlides: centered,
            centeredSlidesBounds: centered,
          },
        },
      });

      // Respond to Swiper boundary events for precise arrow visibility.
      // slideChange alone misses cases where scrolling reaches an edge
      // without crossing a snap point.
      Object.assign(config.on, {
        reachBeginning: () => this.#updateMultiSlideArrows(),
        reachEnd: () => this.#updateMultiSlideArrows(),
        fromEdge: () => this.#updateMultiSlideArrows(),
      });
    } else if (this.hasAttribute('crossfade')) {
      // Crossfade slideshows (hero carousels) with blur during transition
      const maxBlur = 12;

      Object.assign(config, {
        slidesPerView: 1,
        loop: wantsLoop,
        rewind: wantsRewind,
        effect: 'fade',
        fadeEffect: { crossFade: true },
        speed: parseInt(this.getAttribute('crossfade-speed'), 10) || 800,
        ...(this.hasAttribute('auto-height') && { autoHeight: true }),
      });

      const paginationEl = this.querySelector('[data-pagination]');

      if (paginationEl) {
        config.pagination = {
          el: paginationEl,
          clickable: true,
        };
      }

      Object.assign(config.on, {
        setTranslate: () => {
          if (!this.#swiper) return;

          let maxOpacity = 0;

          for (const slide of this.#swiper.slides) {
            const opacity = parseFloat(slide.style.opacity);
            maxOpacity = Math.max(maxOpacity, opacity);
            const blur = maxBlur * (1 - Math.min(Math.max(opacity, 0), 1));
            slide.style.filter = blur > 0.5 ? `blur(${blur.toFixed(1)}px)` : '';
          }

          if (paginationEl) {
            const dotsBlur = maxBlur * 0.5 * (1 - maxOpacity);
            paginationEl.style.filter = dotsBlur > 0.5 ? `blur(${dotsBlur.toFixed(1)}px)` : '';
          }
        },
        setTransition: (_swiper, duration) => {
          if (!this.#swiper) return;

          for (const slide of this.#swiper.slides) {
            slide.style.transitionProperty = 'opacity, filter';
            slide.style.transitionDuration = `${duration}ms`;
          }

          if (paginationEl) {
            paginationEl.style.transitionProperty = 'filter';
            paginationEl.style.transitionDuration = `${duration}ms`;
          }
        },
      });
    } else {
      // Single-slide slideshows (testimonials, announcements)
      Object.assign(config, {
        slidesPerView: 1,
        loop: wantsLoop,
        rewind: wantsRewind,
      });
    }

    // For multi-slide carousels, skip Swiper if all slides fit without scrolling.
    // A ResizeObserver will activate Swiper later if the viewport shrinks.
    if (isMultiSlide && !this.#slidesOverflow(scroller)) {
      this.#pendingConfig = config;
      this.#enterDormantState();
      this.#observeOverflow(scroller);
      this.addEventListener('mouseenter', this.suspend);
      this.addEventListener('mouseleave', this.resume);
      document.addEventListener('visibilitychange', this.#handleVisibilityChange);
      return;
    }

    const Swiper = await loadSwiper();

    // setupSlideshow can be re-entered if disconnect+reconnect happens during
    // the await above (e.g., parent component reinitializing the slideshow).
    // Bail if the element was disconnected, or destroy any existing Swiper
    // before creating a new one to avoid leaking instances on the same DOM.
    if (!this.isConnected) return;
    if (this.#swiper) {
      this.#swiper.destroy(true, true);
      this.#swiper = null;
    }
    this.#swiper = new Swiper(scroller, config);

    // Compositing-layer hover fix for crossfade is handled via CSS
    // (.swiper-slide-active { transform: translateZ(0) }) in hero.liquid.

    // Prevent keyboard focus on Swiper's loop duplicate slides
    if (this.#swiper.params.loop) {
      this.#inertLoopClones();
    }

    // Set slidesPerGroup for group arrow mode
    this.#updateSlidesPerGroup();

    // Wire up autoplay (custom implementation — same timing API as original)
    if (this.autoplay) {
      this.resume();
    }

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();
    this.#updateSlidesFit();
    this.current = this.#swiper.realIndex;

    // Swiper applies inline `margin-right` (spaceBetween) and computes
    // isBeginning/isEnd inside `updateSlides`/`updateProgress`. When the
    // host element was just reconnected (e.g. cart-upsells reinit on
    // drawer open), that work runs against a not-yet-stable layout and
    // the inline gap + boundary state never land on the first paint.
    // Schedule a post-layout update so spacing and arrow state are
    // consistent regardless of when init happens.
    requestAnimationFrame(() => {
      if (!this.#swiper || !this.isConnected) return;
      this.#swiper.update();
      if (this.#isMultiSlide) {
        this.#updateMultiSlideArrows();
        this.#updateControlsVisibility();
      }
    });
  }

  /**
   * Ensures enough slides exist for smooth Swiper loop mode.
   * Reads the minimum-for-loop attribute to determine how many total
   * slides are needed, then clones originals until that count is met.
   * @param {HTMLElement} scroller
   */
  #ensureLoopSlides(scroller) {
    const wrapper = scroller.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    const minimum = parseInt(this.getAttribute('minimum-for-loop'), 10);
    if (!minimum || minimum <= 0) return;

    const originals = /** @type {HTMLElement[]} */ (
      [...wrapper.querySelectorAll('.swiper-slide')]
    );
    if (!originals.length) return;

    let totalSlides = originals.length;
    if (totalSlides >= minimum) return;

    // Clone full sets of originals until we reach the minimum
    while (totalSlides < minimum) {
      for (const slide of originals) {
        const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
        clone.removeAttribute('id');
        clone.removeAttribute('slide-id');
        clone.setAttribute('aria-hidden', 'true');
        wrapper.appendChild(clone);
        totalSlides++;

        if (totalSlides >= minimum) break;
      }
    }
  }


  /**
   * Updates Swiper's slidesPerGroup based on visible slides count.
   * Also sets loopedSlides high enough for Swiper to create sufficient
   * duplicate slides on each end for smooth group-based loop navigation.
   */
  #updateSlidesPerGroup() {
    if (!this.#swiper || this.arrowMode !== 'group') return;

    const visible = this.visibleSlides.length;
    const perGroup = visible > 1 ? visible : 1;

    if (this.#swiper.params.slidesPerGroup !== perGroup) {
      this.#swiper.params.slidesPerGroup = perGroup;

      if (this.#swiper.params.loop) {
        // Swiper needs loopedSlides >= slidesPerGroup for enough duplicates
        const needed = perGroup * 2;
        this.#swiper.params.loopedSlides = Math.max(
          this.#swiper.params.loopedSlides || 0,
          needed
        );
        this.#swiper.loopDestroy();
        this.#swiper.loopCreate();
        this.#swiper.update();
        this.#inertLoopClones();
      }
    }
  }

  /**
   * Checks if all slides fit within the container and sets a data attribute
   * so CSS can expand slides to fill the available space.
   */
  #updateSlidesFit() {
    if (!this.#swiper || !this.#isMultiSlide) return;

    const slides = this.refs.slides;
    if (!slides?.length) return;

    const allVisible = slides.every((slide) =>
      slide.classList.contains('swiper-slide-visible')
    );

    this.toggleAttribute('data-slides-fit', allVisible);
  }

  // ===========================================================================
  // Public API (backwards compatible)
  // ===========================================================================

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Reinitializes the slideshow after external DOM changes (e.g., morph).
   * Destroys the current Swiper and sets up a fresh one from the current DOM.
   */
  reinitialize() {
    this.disconnectedCallback();
    this.connectedCallback();
  }

  /**
   * Selects a slide based on the input.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  select(input, event, options = {}) {
    if (!this.#swiper || !this.refs.slides?.length) return;

    let index = this.#resolveIndex(input);
    if (index === undefined || isNaN(index)) return;

    event?.preventDefault();

    const { animate = true } = options;
    const speed = animate ? this.#swiper.params.speed : 0;

    if (this.#swiper.params.loop) {
      this.#swiper.slideToLoop(index, speed);
    } else {
      // Clamp for non-loop
      const slides = this.slides;
      if (slides) {
        index = Math.max(0, Math.min(index, slides.length - 1));
      }
      this.#swiper.slideTo(index, speed);
    }
  }

  /**
   * Resolves various input types to a slide index.
   * @param {number|string|{id: string}} input
   * @returns {number | undefined}
   */
  #resolveIndex(input) {
    if (typeof input === 'number') return input;
    if (typeof input === 'string') return parseInt(input, 10);

    if (typeof input === 'object' && 'id' in input) {
      const target = this.refs.slides?.find(
        (slide) => slide.getAttribute('slide-id') == input.id
      );

      if (!target || !this.slides) return undefined;

      // Force hidden slides to be revealed
      if (target.hasAttribute('hidden')) {
        target.setAttribute('reveal', '');
        target.setAttribute('aria-hidden', 'false');
      }

      return this.slides.indexOf(target);
    }

    return undefined;
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event]
   * @param {Object} [options]
   * @param {boolean} [options.animate=true]
   */
  next(event, options) {
    event?.preventDefault();
    if (!this.#swiper) return;

    const { animate = true } = options || {};
    this.#swiper.slideNext(animate ? this.#swiper.params.speed : 0);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event]
   * @param {Object} [options]
   * @param {boolean} [options.animate=true]
   */
  previous(event, options) {
    event?.preventDefault();
    if (!this.#swiper) return;

    const { animate = true } = options || {};
    this.#swiper.slidePrev(animate ? this.#swiper.params.speed : 0);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval]
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  /** Pauses automatic slide playback. */
  pause() {
    this.paused = true;
    this.suspend();
  }

  /** Suspends automatic slide playback without marking as paused. */
  suspend = () => {
    clearInterval(this.#interval);
    this.#interval = undefined;
  };

  /** Resumes automatic slide playback if autoplay is enabled. */
  resume = () => {
    if (!this.autoplay || this.paused) return;
    this.suspend();
    this.play();
  };

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /** @type {number} */
  get current() {
    return this.#current;
  }

  /** @param {number} value */
  set current(value) {
    const { current: currentRef, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    // Update counter display
    if (currentRef) currentRef.textContent = `${value + 1}`;

    // Update dot/thumbnail active states
    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    // Update slide aria-hidden
    if (slides) {
      if (this.#isMultiSlide && this.#swiper) {
        for (const slide of slides) {
          const isVisible = slide.classList.contains('swiper-slide-visible');
          slide.setAttribute('aria-hidden', `${!isVisible}`);
        }
      } else {
        for (const [i, slide] of slides.entries()) {
          slide.setAttribute('aria-hidden', `${i !== value}`);
        }
      }
    }

    // Update arrow disabled states
    const canLoop = this.infinite || this.loop;

    if (this.#isMultiSlide && this.#swiper) {
      // For multi-slide, use Swiper's boundary detection since
      // the active index doesn't correspond to the last slide
      if (previous) previous.disabled = Boolean(!canLoop && this.#swiper.isBeginning);
      if (next) next.disabled = Boolean(!canLoop && this.#swiper.isEnd);
    } else {
      if (previous) previous.disabled = Boolean(!canLoop && value === 0);
      if (next) next.disabled = Boolean(!canLoop && slides && value >= slides.length - 1);
    }
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get loop() {
    return this.hasAttribute('loop');
  }

  get visibleSlides() {
    if (!this.#swiper) return [];

    return this.#swiper.slides
      .filter((slide) => slide.classList.contains('swiper-slide-visible'))
      .map((el) => /** @type {HTMLElement} */ (el));
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = this.arrowMode === 'group' && visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = this.arrowMode === 'group' && visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get arrowMode() {
    return this.getAttribute('arrow-mode') || 'single';
  }

  get atStart() {
    const { current, slides } = this;
    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;
    return slides?.length ? current === slides.length - 1 : false;
  }

  /** @param {boolean} value */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }

  /** @type {boolean} */
  get disabled() {
    return this.getAttribute('disabled') === 'true';
  }

  get slides() {
    return this.refs.slides?.filter(
      (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal')
    );
  }

  // ===========================================================================
  // Multi-slide dormant state (no Swiper when slides fit)
  // ===========================================================================

  /**
   * Checks if slide content overflows the scroller container.
   * Compares total natural slide widths (without gaps) against the
   * available content area. If slides alone don't fit, Swiper is needed.
   * If they do fit, dormant mode can use flex-shrink to absorb gaps.
   *
   * Treats a 0-width scroller (e.g. inside a closed dialog) as "fits"
   * so we enter dormant mode and let ResizeObserver activate Swiper
   * once the container becomes visible. This avoids a stale Swiper
   * initialised against a 0-width box that then fights subsequent
   * reinit attempts.
   *
   * @param {HTMLElement} scroller
   * @returns {boolean}
   */
  #slidesOverflow(scroller) {
    /** @type {HTMLElement | null} */
    const wrapper = scroller.querySelector('.swiper-wrapper');
    if (!wrapper) return true;

    /** @type {NodeListOf<HTMLElement>} */
    const slides = wrapper.querySelectorAll(':scope > .swiper-slide');
    if (slides.length < 2) return false;

    if (scroller.offsetWidth === 0) return false;

    const gap = this.#getCurrentGap();
    let totalWidth = 0;

    for (const slide of slides) {
      totalWidth += slide.offsetWidth;
    }

    // Add gaps between slides
    totalWidth += gap * (slides.length - 1);

    const cs = getComputedStyle(scroller);
    const pl = parseFloat(cs.paddingInlineStart) || 0;
    const pr = parseFloat(cs.paddingInlineEnd) || 0;
    const available = scroller.offsetWidth - pl - pr;

    return totalWidth > available;
  }

  /**
   * Returns the current gap value based on viewport width.
   * Mirrors the Swiper spaceBetween breakpoint logic.
   * @returns {number}
   */
  #getCurrentGap() {
    const desktopGap = parseInt(this.getAttribute('data-slide-gap') || '0', 10);
    const mobileGap = Math.min(desktopGap, 12);
    return window.innerWidth >= 750 ? desktopGap : mobileGap;
  }

  /**
   * Sets up dormant state for multi-slide carousels where all slides fit.
   * Uses CSS flex layout with gap instead of Swiper.
   */
  #enterDormantState() {
    this.toggleAttribute('data-slides-dormant', true);
    this.current = 0;

    // Apply gap on wrapper for proper visual spacing between slides
    /** @type {HTMLElement | null} */
    const wrapper = this.refs.scroller?.querySelector('.swiper-wrapper') ?? null;
    if (wrapper) {
      wrapper.style.columnGap = `${this.#getCurrentGap()}px`;
    }

    // Mark all slides as visible
    const slides = this.refs.slides;
    if (slides) {
      for (const slide of slides) {
        slide.setAttribute('aria-hidden', 'false');
      }
    }

    // Disable arrows — nothing to scroll
    const { previous, next } = this.refs;
    if (previous) previous.disabled = true;
    if (next) next.disabled = true;

    if (this.hasAttribute('auto-hide-controls')) {
      const arrows = this.querySelector('slideshow-arrows');
      if (arrows) arrows.hidden = true;
    }
  }

  /**
   * Sets up a ResizeObserver on the scroller to activate Swiper
   * when slides begin to overflow on viewport shrink.
   * @param {HTMLElement} scroller
   */
  #observeOverflow(scroller) {
    let lastWidth = 0;

    this.#resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? 0;
      const becameVisible = lastWidth === 0 && width > 0;
      lastWidth = width;

      clearTimeout(this.#resizeTimeout);

      // Skip debounce when transitioning from hidden to visible
      // (e.g. content-visibility: auto → visible on mega menu open).
      // Normal viewport resizes still get the 200ms debounce.
      const delay = becameVisible ? 0 : 200;

      this.#resizeTimeout = setTimeout(() => {
        if (!this.isConnected || this.#swiper) return;

        /** @type {HTMLElement | null} */
        const wrapper = scroller.querySelector('.swiper-wrapper');
        if (!wrapper) return;

        // Temporarily remove dormant layout to measure natural overflow
        const gap = wrapper.style.columnGap;
        wrapper.style.columnGap = '';
        this.removeAttribute('data-slides-dormant');

        if (this.#slidesOverflow(scroller)) {
          this.#activateSwiper();
        } else {
          // Restore dormant state
          wrapper.style.columnGap = gap;
          this.toggleAttribute('data-slides-dormant', true);
        }
      }, delay);
    });
    this.#resizeObserver.observe(scroller);
  }

  /**
   * Initializes Swiper from stored config (dormant → active transition).
   * Called by the ResizeObserver when slides begin to overflow.
   */
  async #activateSwiper() {
    const { scroller } = this.refs;
    if (!scroller || !this.#pendingConfig || this.#swiper) return;

    // Clean up dormant state
    this.removeAttribute('data-slides-dormant');
    /** @type {HTMLElement | null} */
    const wrapper = scroller.querySelector('.swiper-wrapper');
    if (wrapper) wrapper.style.columnGap = '';

    // Stop observing — Swiper handles resize from here
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    clearTimeout(this.#resizeTimeout);

    const Swiper = await loadSwiper();
    if (!this.isConnected) return;
    if (this.#swiper) {
      this.#swiper.destroy(true, true);
      this.#swiper = null;
    }
    this.#swiper = new Swiper(scroller, this.#pendingConfig);
    this.#updateSlidesPerGroup();
    this.#updateMultiSlideArrows();
    this.#updateControlsVisibility();
    this.#updateSlidesFit();
    this.current = this.#swiper.realIndex;

    // See setupSlideshow — defer update so spacing + arrow state are
    // measured against the post-layout DOM, not the transitional one.
    requestAnimationFrame(() => {
      if (!this.#swiper || !this.isConnected) return;
      this.#swiper.update();
      this.#updateMultiSlideArrows();
      this.#updateControlsVisibility();
    });

    if (this.autoplay) this.resume();
  }

  /**
   * Updates arrow disabled state using Swiper's boundary detection.
   * Called by reachBeginning, reachEnd, and fromEdge events.
   */
  #updateMultiSlideArrows() {
    if (!this.#swiper) return;

    const canLoop = this.infinite || this.loop;
    const { previous, next } = this.refs;

    if (previous) previous.disabled = Boolean(!canLoop && this.#swiper.isBeginning);
    if (next) next.disabled = Boolean(!canLoop && this.#swiper.isEnd);
  }

  // ===========================================================================
  // Internal handlers
  // ===========================================================================

  #onSlideChange() {
    if (!this.#swiper) return;

    const index = this.#swiper.realIndex;

    // Guard against redundant calls (watchSlidesProgress + loop can spam this)
    if (index === this.#current) return;

    const previousIndex = this.#current;
    const slides = this.slides;
    const slide = slides?.[index];

    this.current = index;

    if (slide) {
      this.dispatchEvent(
        new SlideshowSelectEvent({
          index,
          previousIndex,
          userInitiated: true,
          trigger: 'scroll',
          slide,
          id: slide.getAttribute('slide-id'),
        })
      );
    }

    this.#centerSelectedThumbnail(index);
  }

  #onTransitionEnd() {
    this.removeAttribute('transitioning');
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.suspend();
    } else {
      this.resume();
    }
  };

  /**
   * @param {number} index
   */
  #centerSelectedThumbnail(index) {
    const { thumbnails } = this.refs;
    if (!thumbnails?.length) return;

    const thumb = thumbnails[index];
    if (!thumb) return;

    thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller } = this.refs;
    if (!scroller) return;

    const noOverflow = scroller.scrollWidth <= scroller.offsetWidth;

    const { slideshowControls } = this.refs;
    if (slideshowControls instanceof HTMLElement) {
      slideshowControls.hidden = noOverflow;
    }

    const arrows = this.querySelector('slideshow-arrows');
    if (arrows) arrows.hidden = noOverflow;
  }

  /**
   * Sets the `inert` attribute on Swiper's loop duplicate slides so they
   * are removed from the tab order and accessibility tree entirely.
   * This prevents keyboard users from getting trapped in infinite clones.
   */
  #inertLoopClones() {
    if (!this.#swiper) return;

    for (const slide of this.#swiper.slides) {
      if (slide.classList.contains('swiper-slide-duplicate')) {
        slide.inert = true;
      }
    }
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
